import secureLocalStorage from "react-secure-storage";
import i18next from "i18next";
import * as StorageConstants from "../../Models/SecureStorageConstants";
import { CustomerStyleService, IBrowserBrandingModel } from "../../Services/customerStyleService";
import { getStoredAccessToken } from "../../Util/secureStorageSession";
import defaultFavicon from "../../Images/tablogo.png";

import { readUrlSpecificStylingCache, writeUrlSpecificStylingCache } from "./urlSpecificStyling";

export const BROWSER_TITLE_MAX_LENGTH = 80;
export const EARLY_TAB_BRANDING_KEY = "dashly.tabBranding";

const ICON_REL_SELECTOR = "link[rel='icon']";

export const normalizeApplicationUrl = (value?: string | null): string => {
	if (!value) {
		return "";
	}
	return value.trim().replace(/\/+$/, "").toLowerCase();
};

export const matchesCurrentOrigin = (applicationUrl?: string | null): boolean => {
	return normalizeApplicationUrl(applicationUrl) === normalizeApplicationUrl(window.location.origin);
};

export const readBrowserBrandingCache = (): IBrowserBrandingModel | null => {
	const stored = secureLocalStorage.getItem(StorageConstants.BROWSER_BRANDING);
	if (!stored) {
		return null;
	}
	try {
		const parsed = typeof stored === "string" ? JSON.parse(stored) : stored;
		if (!parsed || typeof parsed !== "object") {
			return null;
		}
		return {
			browserTitle: parsed.browserTitle ?? null,
			favicon: parsed.favicon ?? null,
		};
	} catch {
		return null;
	}
};

export const writeBrowserBrandingCache = (branding: IBrowserBrandingModel): void => {
	const payload = {
		browserTitle: branding.browserTitle ?? null,
		favicon: branding.favicon ?? null,
	};
	secureLocalStorage.setItem(StorageConstants.BROWSER_BRANDING, JSON.stringify(payload));
};

const writeEarlyTabBranding = (title: string, favicon: string): void => {
	try {
		window.localStorage.setItem(EARLY_TAB_BRANDING_KEY, JSON.stringify({
			browserTitle: title,
			favicon,
		}));
	} catch {
		// localStorage may be unavailable
	}
};

const defaultBrowserTitle = (): string => i18next.t("title") || "Dashly";

export const applyBrowserBranding = (branding?: IBrowserBrandingModel | null): void => {
	const title = branding?.browserTitle?.trim() || defaultBrowserTitle();
	document.title = title;

	const favicon = branding?.favicon?.trim() || defaultFavicon;
	let iconLink = document.querySelector(ICON_REL_SELECTOR) as HTMLLinkElement | null;
	if (!iconLink) {
		iconLink = document.createElement("link");
		iconLink.rel = "icon";
		document.head.appendChild(iconLink);
	}
	iconLink.type = branding?.favicon?.trim() ? inferFaviconType(branding.favicon) : "image/png";
	iconLink.href = favicon;
	writeEarlyTabBranding(title, favicon);
};

export const applyCachedBrowserBranding = (): boolean => {
	const cached = readBrowserBrandingCache();
	if (!cached?.browserTitle && !cached?.favicon) {
		return false;
	}
	applyBrowserBranding(cached);
	return true;
};

export const writeThroughBrowserBranding = (branding: IBrowserBrandingModel): void => {
	writeBrowserBrandingCache(branding);
	applyBrowserBranding(branding);
};

export const clearStoragePreservingBrowserBranding = (): void => {
	const branding = readBrowserBrandingCache();
	// Same treatment for the URL-specific CSS cache: after logout the sign-in page must
	// still paint branded on its first frame instead of waiting for the backend.
	const urlStyling = readUrlSpecificStylingCache();
	secureLocalStorage.clear();
	if (branding?.browserTitle || branding?.favicon) {
		writeBrowserBrandingCache(branding);
	}
	if (urlStyling !== null) {
		writeUrlSpecificStylingCache(urlStyling || null);
	}
};

const extractBranding = (response: {
	data?: { data?: IBrowserBrandingModel | null } | IBrowserBrandingModel | null;
}): IBrowserBrandingModel => {
	const payload = (response?.data && typeof response.data === "object" && "data" in response.data
		? response.data.data
		: response?.data) as IBrowserBrandingModel | null | undefined;
	const record = payload && typeof payload === "object" ? payload as IBrowserBrandingModel & { browser_title?: string } : null;
	return {
		browserTitle: record?.browserTitle?.trim() || record?.browser_title?.trim() || null,
		favicon: record?.favicon?.trim() || null,
	};
};

const applyFetchedBranding = (response: { data?: { data?: IBrowserBrandingModel | null } | null }): void => {
	writeThroughBrowserBranding(extractBranding(response));
};

export const reloadBrowserBrandingFromOrigin = async (): Promise<void> => {
	try {
		const response = await new CustomerStyleService().fetchUrlSpecificBranding();
		if (getStoredAccessToken()) {
			return;
		}
		applyFetchedBranding(response);
	} catch {
		if (getStoredAccessToken()) {
			return;
		}
		if (!applyCachedBrowserBranding()) {
			applyBrowserBranding(null);
		}
	}
};

export const reloadBrowserBrandingForSession = async (): Promise<void> => {
	try {
		applyFetchedBranding(await new CustomerStyleService().fetchSessionBranding());
	} catch {
		if (!applyCachedBrowserBranding()) {
			applyBrowserBranding(null);
		}
	}
};

i18next.on("languageChanged", () => {
	applyCachedBrowserBranding();
});

export const loadAndApplyBrowserBranding = async (): Promise<void> => {
	if (getStoredAccessToken()) {
		if (applyCachedBrowserBranding()) {
			return;
		}
		await reloadBrowserBrandingForSession();
		return;
	}
	applyCachedBrowserBranding();
	await reloadBrowserBrandingFromOrigin();
};

const inferFaviconType = (favicon: string): string => {
	const lower = favicon.toLowerCase();
	if (lower.startsWith("data:image/svg+xml")) {
		return "image/svg+xml";
	}
	if (lower.startsWith("data:image/x-icon") || lower.startsWith("data:image/vnd.microsoft.icon")
		|| lower.startsWith("data:image/ico")) {
		return "image/x-icon";
	}
	return "image/png";
};
